import { fetchAuth } from "../../../shared/api/ApiService"
import {
    ITutorPaymentDetail,
} from "../../tutor";
import {
    IIdmCreateStaffMember,
    IIdmEffectiveFeature,
    IIdmFeature,
    IIdmRole,
    IIdmStaffListItem,
    IIdmStaffMember,
    IIdmUserFeatureGrant,
    IIdmUserRole,
} from "../model/types"
import { IBusinessLabel } from '../../../shared/model/labels'
import { formatFeatureText } from '../model/formatFeatureText'

interface IIdmRoleResponse {
    id: string;
    code: string;
    name: string;
    hierarchyLevel: number;
    description: string;
}

interface IIdmFeatureResponse {
    id: string;
    key: string;
    category: string;
    name: string;
    description: string;
}

interface IIdmUserRoleResponse {
    roleId: string;
    roleName: string;
    roleCode: string;
}

interface IIdmEffectiveFeatureResponse {
    featureId: string;
    featureKey: string;
    featureName: string;
    category: string;
}

interface IIdmUserFeatureGrantResponse {
    featureId: string;
    featureKey: string;
    category: string;
    featureName: string;
    description: string;
    targetUserIds: string[];
}

interface IIdmStaffListItemResponse {
    userId: string;
    isTutor?: boolean;
    fullName: string;
    email: string;
    roles: string[];
    labels?: IBusinessLabel[];
}

interface IIdmStaffMemberResponse {
    userId: string;
    fullName: string;
    email: string;
    phone: string;
    tg: string;
    isActive: boolean;
    isArchive: boolean;
    legacyAdminId: string;
    legacyRoleId: string;
    roles: IIdmUserRoleResponse[];
    effectiveFeatures: IIdmEffectiveFeatureResponse[];
}

interface IStaffListResponse {
    items: IIdmStaffListItemResponse[];
    total: string;
}

interface IFeatureListResponse {
    features: IIdmFeatureResponse[];
    total: string;
}

const mapRole = (role: IIdmRoleResponse): IIdmRole => ({
    id: Number(role.id),
    code: role.code,
    name: role.code === 'tutor' ? 'Исполнитель' : role.name,
    hierarchy_level: role.hierarchyLevel,
    description: role.description,
})

const mapFeature = (feature: IIdmFeatureResponse): IIdmFeature => ({
    id: Number(feature.id),
    key: feature.key,
    category: feature.category,
    name: formatFeatureText(feature.name),
    description: formatFeatureText(feature.description),
})

const mapUserRole = (role: IIdmUserRoleResponse): IIdmUserRole => ({
    role_id: Number(role.roleId),
    role_name: role.roleCode === 'tutor' ? 'Исполнитель' : role.roleName,
    role_code: role.roleCode,
})

const mapEffectiveFeature = (feature: IIdmEffectiveFeatureResponse): IIdmEffectiveFeature => ({
    feature_id: Number(feature.featureId),
    feature_key: feature.featureKey,
    feature_name: formatFeatureText(feature.featureName),
    category: feature.category,
})

const mapUserFeatureGrant = (feature: IIdmUserFeatureGrantResponse): IIdmUserFeatureGrant => ({
    feature_id: Number(feature.featureId),
    feature_key: feature.featureKey,
    category: feature.category,
    feature_name: formatFeatureText(feature.featureName),
    description: formatFeatureText(feature.description),
    target_user_ids: feature.targetUserIds.map(Number),
})

const mapStaffListItem = (item: IIdmStaffListItemResponse): IIdmStaffListItem => ({
    user_id: Number(item.userId),
    is_tutor: item.isTutor,
    full_name: item.fullName,
    email: item.email,
    roles: item.roles,
    labels: Array.isArray(item.labels) ? item.labels : [],
})

const mapStaffMember = (member: IIdmStaffMemberResponse): IIdmStaffMember => ({
    user_id: Number(member.userId),
    full_name: member.fullName,
    email: member.email,
    phone: member.phone,
    tg: member.tg,
    is_active: member.isActive,
    is_archive: member.isArchive,
    legacy_admin_id: Number(member.legacyAdminId),
    legacy_role_id: Number(member.legacyRoleId),
    roles: member.roles.map(mapUserRole),
    effective_features: member.effectiveFeatures.map(mapEffectiveFeature),
})

const mapStaffListResponse = (data: IStaffListResponse) => ({
    items: data.items.map(mapStaffListItem),
    total: Number(data.total),
})

const mapFeatureListResponse = (data: IFeatureListResponse) => ({
    features: data.features.map(mapFeature),
    total: Number(data.total),
})

const mapPaymentDetailRequest = (detail: ITutorPaymentDetail) => ({
    id: detail.id > 0 ? detail.id : 0,
    type: detail.type,
    recipient_name: detail.recipient_name,
    card_number: detail.card_number,
    phone_number: detail.phone_number,
    bank_name: detail.bank_name,
    account_number: detail.account_number,
    bik: detail.bik,
    inn: detail.inn,
    is_priority: detail.is_priority,
})

class IdmService {
    private listStaffRequests = new Map<string, Promise<{items: IIdmStaffListItem[]; total: number}>>()
    private listStaffCache = new Map<string, {items: IIdmStaffListItem[]; total: number}>()
    private listArchivedStaffRequest: Promise<{items: IIdmStaffListItem[]; total: number}> | null = null
    private listArchivedStaffCache: {items: IIdmStaffListItem[]; total: number} | null = null
    private listRolesRequest: Promise<IIdmRole[]> | null = null
    private listRolesCache: IIdmRole[] | null = null
    private listFeaturesRequests = new Map<string, Promise<{features: IIdmFeature[]; total: number}>>()
    private listFeaturesCache = new Map<string, {features: IIdmFeature[]; total: number}>()

    private clearStaffCache() {
        this.listStaffRequests.clear()
        this.listStaffCache.clear()
        this.listArchivedStaffRequest = null
        this.listArchivedStaffCache = null
    }

    private clearRolesCache() {
        this.listRolesRequest = null
        this.listRolesCache = null
    }

    private clearFeaturesCache() {
        this.listFeaturesRequests.clear()
        this.listFeaturesCache.clear()
    }

    private clearAllListCaches() {
        this.clearStaffCache()
        this.clearRolesCache()
        this.clearFeaturesCache()
    }

    invalidateStaffListCache() {
        this.clearStaffCache()
    }

    async listStaff(roleIds: number[] = [], workTgIds: number[] = [], withoutTg = false, tagIds: number[] = []): Promise<{items: IIdmStaffListItem[]; total: number}> {
        const requestKey = JSON.stringify({roleIds, workTgIds, withoutTg, tagIds})
        const cachedValue = this.listStaffCache.get(requestKey)
        if(cachedValue) return cachedValue

        const currentRequest = this.listStaffRequests.get(requestKey)
        if(currentRequest) return currentRequest

        const request = (async () => {
            const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/api/v1/idm/staff', {
                method: 'POST',
                body: JSON.stringify({roleIds, workTgIds, withoutTg, tagIds}),
            })
            const result = mapStaffListResponse(await res.json() as IStaffListResponse)
            this.listStaffCache.set(requestKey, result)
            return result
        })()

        this.listStaffRequests.set(requestKey, request)

        try{
            return await request
        }
        finally{
            this.listStaffRequests.delete(requestKey)
        }
    }

    async getStaffMember(userId: number): Promise<IIdmStaffMember> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/idm/staff/${userId}`)
        const data = await res.json() as {staffMember: IIdmStaffMemberResponse}
        return mapStaffMember(data.staffMember)
    }

    async listArchivedStaff(tagIds: number[] = []): Promise<{items: IIdmStaffListItem[]; total: number}> {
        // Archive responses depend on the selected tags, so they must not share the
        // unfiltered archive cache.
        if(tagIds.length === 0 && this.listArchivedStaffCache) return this.listArchivedStaffCache
        if(tagIds.length === 0 && this.listArchivedStaffRequest) return this.listArchivedStaffRequest

        const request = (async () => {
            const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/api/v1/idm/staff-archive', {
                method: 'POST',
                body: JSON.stringify({tagIds}),
            })
            const result = mapStaffListResponse(await res.json() as IStaffListResponse)
            if(tagIds.length === 0){
                this.listArchivedStaffCache = result
            }
            return result
        })()

        if(tagIds.length === 0){
            this.listArchivedStaffRequest = request
        }

        try{
            return await request
        }
        finally{
            if(tagIds.length === 0){
                this.listArchivedStaffRequest = null
            }
        }
    }

    async listAvailableCoordinators(workTgIds: number[] = []): Promise<{items: IIdmStaffListItem[]; total: number}> {
        const params = new URLSearchParams()
        workTgIds.filter(id => id > 0).forEach(id => params.append('work_tg_ids', String(id)))
        const query = params.toString()
        const res = await fetchAuth(
            process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/idm/staff/available-coordinators${query ? `?${query}` : ''}`
        )
        return mapStaffListResponse(await res.json() as IStaffListResponse)
    }

    async listAvailableCurators(workTgIds: number[] = []): Promise<{items: IIdmStaffListItem[]; total: number}> {
        const params = new URLSearchParams()
        workTgIds.filter(id => id > 0).forEach(id => params.append('work_tg_ids', String(id)))
        const query = params.toString()
        const res = await fetchAuth(
            process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/idm/staff/available-curators${query ? `?${query}` : ''}`
        )
        return mapStaffListResponse(await res.json() as IStaffListResponse)
    }

    async listRoles(): Promise<IIdmRole[]> {
        if(this.listRolesCache) return this.listRolesCache
        if(this.listRolesRequest) return this.listRolesRequest

        const request = (async () => {
            const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/api/v1/idm/roles')
            const data = await res.json() as {roles: IIdmRoleResponse[]}
            const result = data.roles.map(mapRole)
            this.listRolesCache = result
            return result
        })()

        this.listRolesRequest = request

        try{
            return await request
        }
        finally{
            this.listRolesRequest = null
        }
    }

    async listAvailableRolesToAdd(userId: number): Promise<IIdmRole[]> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/idm/staff/${userId}/available_to_add_roles`)
        const data = await res.json() as {roles: IIdmRoleResponse[]}
        return data.roles.map(mapRole)
    }

    async listRoleFeatures(roleId: number): Promise<{features: IIdmFeature[]; total: number}> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/idm/roles/${roleId}/features`)
        return mapFeatureListResponse(await res.json() as IFeatureListResponse)
    }

    async listRoleFeaturesForCreation(roleId: number): Promise<{features: IIdmFeature[]; total: number}> {
        const res = await fetchAuth(
            process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/idm/roles/${roleId}/features_for_creation`
        )
        return mapFeatureListResponse(await res.json() as IFeatureListResponse)
    }

    async listFeatures(query = '', category = ''): Promise<{features: IIdmFeature[]; total: number}> {
        const requestKey = JSON.stringify({query, category})
        const cachedValue = this.listFeaturesCache.get(requestKey)
        if(cachedValue) return cachedValue

        const currentRequest = this.listFeaturesRequests.get(requestKey)
        if(currentRequest) return currentRequest

        const params = new URLSearchParams()
        if(query) params.set('query', query)
        if(category) params.set('category', category)
        const suffix = params.toString() ? `?${params.toString()}` : ''

        const request = (async () => {
            const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/idm/features${suffix}`)
            const result = mapFeatureListResponse(await res.json() as IFeatureListResponse)
            this.listFeaturesCache.set(requestKey, result)
            return result
        })()

        this.listFeaturesRequests.set(requestKey, request)

        try{
            return await request
        }
        finally{
            this.listFeaturesRequests.delete(requestKey)
        }
    }

    async listUserFeatureGrants(userId: number): Promise<{feature_grants: IIdmUserFeatureGrant[]; total: number}> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/idm/staff/${userId}/features`)
        const data = await res.json() as {featureGrants: IIdmUserFeatureGrantResponse[]; total: string}
        return {
            feature_grants: data.featureGrants.map(mapUserFeatureGrant),
            total: Number(data.total),
        }
    }

    async listUserAvailableFeatures(userId: number): Promise<{features: IIdmFeature[]; total: number}> {
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/idm/staff/${userId}/available-features`)
        return mapFeatureListResponse(await res.json() as IFeatureListResponse)
    }

    async replaceUserFeatureTargets(userId: number, featureId: number, targetUserIds: number[]): Promise<void> {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/idm/staff/${userId}/features/${featureId}/targets/replace`, {
            method: 'POST',
            body: JSON.stringify({userId, featureId, targetUserIds}),
        })
        this.clearStaffCache()
    }

    async assignUserRole(userId: number, roleId: number): Promise<void> {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/idm/staff/${userId}/roles/assign`, {
            method: 'POST',
            body: JSON.stringify({userId, roleId}),
        })
        this.clearStaffCache()
    }

    async removeUserRole(userId: number, roleId: number): Promise<void> {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/idm/staff/${userId}/roles/remove`, {
            method: 'POST',
            body: JSON.stringify({userId, roleId}),
        })
        this.clearStaffCache()
    }

    async grantUserFeature(userId: number, featureId: number): Promise<void> {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/idm/staff/${userId}/features/grant`, {
            method: 'POST',
            body: JSON.stringify({userId, featureId}),
        })
        this.clearStaffCache()
    }

    async revokeUserFeature(userId: number, featureId: number): Promise<void> {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/idm/staff/${userId}/features/revoke`, {
            method: 'POST',
            body: JSON.stringify({userId, featureId}),
        })
        this.clearStaffCache()
    }

    async addRoleFeature(roleId: number, featureId: number): Promise<void> {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/idm/roles/${roleId}/features/add`, {
            method: 'POST',
            body: JSON.stringify({roleId, featureId}),
        })
        this.clearFeaturesCache()
    }

    async removeRoleFeature(roleId: number, featureId: number): Promise<void> {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/idm/roles/${roleId}/features/remove`, {
            method: 'POST',
            body: JSON.stringify({roleId, featureId}),
        })
        this.clearFeaturesCache()
    }

    async resetUserPassword(userId: number): Promise<void> {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/idm/staff/${userId}/password/reset`, {
            method: 'POST',
            body: JSON.stringify({userId}),
        })
    }

    async deleteUser(userId: number): Promise<void> {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/idm/staff/${userId}/delete`, {
            method: 'POST',
            body: JSON.stringify({userId}),
        })
        this.clearStaffCache()
    }

    async saveStaffPaymentDetails(userId: number, paymentDetails: ITutorPaymentDetail[]): Promise<void> {
        await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + `/api/v1/idm/staff/${userId}/payment_details`, {
            method: 'POST',
            body: JSON.stringify({
                payment_details: paymentDetails.map(mapPaymentDetailRequest),
            }),
        })
        this.clearStaffCache()
    }

    async createStaffMember(staff: IIdmCreateStaffMember): Promise<number> {
        const tutorMeta = staff.tutor_meta
            ? {
                costPerHour: staff.tutor_meta.cost_per_hour,
                costPerShift: staff.tutor_meta.cost_per_shift,
                desiredHours: staff.tutor_meta.desired_hours,
                tagIds: staff.tutor_meta.tag_ids,
                gender: staff.tutor_meta.gender,
            }
            : undefined
        const res = await fetchAuth(process.env.REACT_APP_SERVER_URL_ADMIN + '/api/v1/users/create', {
            method: 'POST',
            body: JSON.stringify({
                email: staff.email,
                fullName: staff.full_name,
                phone: staff.phone,
                tg: staff.tg,
                idmRoleIds: staff.idm_role_ids,
                idmFeatureIds: staff.idm_feature_ids,
                relations: staff.relations.map(relation => ({
                    relatedUserId: relation.related_user_id,
                    relationType: relation.relation_type,
                })),
                workTgIds: staff.work_tg_ids,
                tutorMeta,
                costPerMonth: staff.cost_per_month,
            }),
        })
        const data = await res.json() as {userId: string}
        this.clearAllListCaches()
        return Number(data.userId)
    }
}

export const idmService = new IdmService()
